<template>
    <div
        v-if="visibleAttributes.length || selectedAttributes.length"
        :class="`af-attribute-filter-${attributeKey} af-attribute-filter afu-my-4 afu-pt-5 afu-pb-2 afu-px-4 afu-shadow-secondary afu-rounded-secondary afu-border-neutral-light afu-bg-neutral-light`"
    >
        <C1Label
            class="afu-mb-3 afu-text-5xl afu-uppercase afu-text-secondary afu-font-bold afu-tracking-widest"
        >
            <span class="af-text--5xl afu-uppercase afu-font-bold afu-tracking-widest">{{ attributeLabel }}</span>
        </C1Label>
        <div
            v-if="showTypeahead"
            class="afu-mb-3"
        >
            <C1Typeahead
                :options="options"
                :placeholder="$t('label.search_within_value', { 'value1': attributes.length, 'value2': attributeLabel})"
                clean-after-select
                no-icon
                @search="searchAttributes"
                label-key="value"
                value-key="id"
                @hit="onSelect"
            />
        </div>

        <ul class="afu-m-0 afu-p-0 afu-list-none">
            <li
                v-for="(selectedAttribute, index) in selectedAttributes"
                :key="`${selectedAttribute.id}_${index}`"
                class="afu-text-base afu-text-black afu-mb-3 afu-flex afu-items-center"
            >
                <div
                    class="afu-cursor-pointer afu-flex afu-items-center"
                    @click="onSelect(selectedAttribute)"
                >
                    <div class="afu-text-lg">
                        <FontAwesomeIcon
                            :icon="['fas', 'check-square']"
                            class="afu-mr-2 afu-text-secondary fa-w-16"
                        />
                    </div>

                    <div class="afu-text-sm">
                        {{ selectedAttribute.value }}
                    </div>
                </div>
            </li>
            <li
                v-for="(visibleAttribute, index) in visibleAttributes"
                :key="`${visibleAttribute.id}_${index}`"
                class="afu-text-sm afu-mb-3 afu-flex afu-items-center"
            >
                <div
                    class="afu-cursor-pointer afu-flex afu-items-center"
                    @click="onSelect(visibleAttribute)"
                >
                    <div class="afu-text-lg">
                        <FontAwesomeIcon
                            :icon="['far', 'square']"
                            class="afu-mr-2 afu-text-secondary fa-w-16"
                        />
                    </div>

                    <div class="afu-text-sm">
                        {{ visibleAttribute.value }} ({{ visibleAttribute.count | numeral }})
                    </div>
                </div>
            </li>
        </ul>

        <div
            v-if="showTypeahead"
            class="afu-text-sm afu-text-gray-800 afu-bg-gray-300 afu-rounded-md afu-text-center afu-p-2 afu-mt-2"
        >
            {{ $t('filter.info') }}
        </div>
    </div>
</template>

<script>
    import { mapState, mapMutations } from 'vuex';
    import numeral from 'numeral';

    import C1Label from '@/components/C1Components/C1Label';
    import C1Typeahead from '@/components/C1Components/C1Typeahead';

    export default {
        name: 'AttributeFilter',

        filters: {
            numeral(value) {
                if (!value) { return; }
                return numeral(value).format('0,0');
            },
        },

        components: {
            C1Label,
            C1Typeahead,
        },

        props: {
            attributeKey: {
                type: String,
                required: true,
            },
        },

        data() {
            return {
                maxVisible: 15,
                options: [],
                customAttributes: {
                    skills : 'Skills',
                    job_type : this.$t('label.work_type'),
                    contract_type : this.$tc('label.contract_type', 2),
                    brands : 'Company Name',
                },
            };
        },

        computed: {
            ...mapState({
                attributes(state) {
                    return state.search.searchPayload.search_results.aggregation[this.attributeKey] || [];
                },
                selectedAttributes(state) {
                    return state.search.searchPayload.search_filters[this.attributeKey] || [];
                },
            }),

            showTypeahead() {
                return this.attributes.length > this.maxVisible;
            },

            visibleAttributes() {
                const selectedAttributeIds = this.selectedAttributes.map(o => o.id);
                return this.attributes.filter(o => !selectedAttributeIds.includes(o.id)).slice(0, this.maxVisible - this.selectedAttributes.length);
            },

            attributeLabel() {
                return this.customAttributes[this.attributeKey] ? this.customAttributes[this.attributeKey] : 'Custom Attributes';
            },
        },

        methods: {
            ...mapMutations('search', {
                $_STORE_SEARCH_FILTER_ATTRIBUTE: 'STORE_SEARCH_FILTER_ATTRIBUTE',
            }),

            onSelect(attributeOption) {
                this.$_STORE_SEARCH_FILTER_ATTRIBUTE({ key: this.attributeKey, attributeOption: attributeOption });
            },

            searchAttributes(keyword) {
                const selectedAttributeIds = this.selectedAttributes.map(o => o.id);
                this.options = this.attributes.filter(o => o.value.toLowerCase().startsWith(keyword) && !selectedAttributeIds.includes(o.id));
            },
        },
    };
</script>
